import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../hooks/hooks";
import { dataSearchStatus } from "../../../store/dataSearchSlice";
import { useLoader } from "../../../hooks/common/useLoader";
import { ListTable } from "../../../layout/content/list/ListTable";
import excelIcon from "../../../assets/images/button_icons/excel_icon.svg";
import { useDataSearchFilters } from "../hooks/useDataSearchFilters";
import { useTestResultColumns } from "../hooks/useTestResultColumns";
import { useTestResultList } from "../hooks/useTestResultList";
import { useTestResultDownloader } from "../hooks/useTestResultDownloader";
import { TestResultSearchCondition } from "../../../types/dataSearch";
import { ColumnSelector } from "./ColumnSelector";

export function TestResultSearch() {
  const { t } = useTranslation("dataSearch");
  const status = useAppSelector(dataSearchStatus);

  useLoader({ status });

  const { searchTestResultList, totalCnt, testResultList, pageCondition, getSearchParam } = useTestResultList({});
  const { filters } = useDataSearchFilters({ search: searchTestResultList({}) });
  const { columns } = useTestResultColumns();
  const { downloadTestResults } = useTestResultDownloader();

  const columnMetas = useMemo(
    () => columns.map((c) => ({ key: String(c.accessor), label: String(c.header) })),
    [columns]
  );

  const [selectedKeys, setSelectedKeys] = useState<string[]>(() => columns.map((c) => String(c.accessor)));

  const visibleColumns = useMemo(
    () => columns.filter((c) => selectedKeys.includes(String(c.accessor))),
    [columns, selectedKeys]
  );

  const onClickDownload = useCallback(() => {
    const condition = {
      ...(getSearchParam() as unknown as TestResultSearchCondition),
      columns: selectedKeys,
    };
    downloadTestResults(condition);
  }, [downloadTestResults, getSearchParam, selectedKeys]);

  return (
    <>
      <ColumnSelector columns={columnMetas} selectedKeys={selectedKeys} onChange={setSelectedKeys} />
      {testResultList ? (
        <ListTable
          pageOnFilter={pageCondition}
          filters={filters}
          totalRowsCnt={{
            totalCount: <span>{t("list.totalLabel", { count: totalCnt ?? 0 })}</span>,
            singleUnitKeyInCommonFile: "testHistoryUnit.singleComponent",
            multipleUnitKeyInCommonFile: "testHistoryUnit.multipleComponent",
          }}
          columns={visibleColumns}
          listData={testResultList}
          getList={({ page, sort, size }) => searchTestResultList({ page, sort, size })}
          buttonsNode={
            <>
              <div></div>
              <div>
                <button onClick={onClickDownload}>
                  <img src={excelIcon} alt={t("list.downloadExcel.iconAlt")} />
                  {t("list.downloadExcel.buttonLabel")}
                </button>
              </div>
            </>
          }
        />
      ) : (
        <></>
      )}
    </>
  );
}
